import React, { useId } from 'react';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  options?: SelectOption[];
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  error?: string;
  fullWidth?: boolean;
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ options, value, onChange, placeholder, error, fullWidth = true, className, style, id: propId, ...props }, ref) => {
    const generatedId = useId();
    const inputId = propId || generatedId;
    const hasError = !!error;

    return (
      <div className={fullWidth ? 'w-full' : ''}>
        <select
          ref={ref}
          id={inputId}
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          className={`w-full h-9 rounded-lg border px-3 py-1.5 text-sm appearance-none focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 ${className || ''}`}
          style={{
            backgroundColor: 'var(--theme-input-background)',
            borderColor: hasError ? 'var(--theme-error-color)' : 'var(--theme-input-border)',
            color: value ? 'var(--theme-input-text)' : 'var(--theme-muted-text)',
            transition: 'border-color 150ms, box-shadow 150ms',
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 0.75rem center',
            backgroundSize: '1rem',
            paddingRight: '2.5rem',
            ...style,
          }}
          onFocus={(e) => {
            if (!hasError && !props.disabled) {
              e.currentTarget.style.borderColor = 'var(--theme-input-focus)';
              e.currentTarget.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.15)';
            }
            props.onFocus?.(e);
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = hasError ? 'var(--theme-error-color)' : 'var(--theme-input-border)';
            e.currentTarget.style.boxShadow = 'none';
            props.onBlur?.(e);
          }}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>{placeholder}</option>
          )}
          {options?.map((opt) => (
            <option key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </option>
          ))}
        </select>
        {error && (
          <p className="text-xs mt-1" style={{ color: 'var(--theme-error-color)' }}>{error}</p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';
